import KeyboardButton from "app/spelling-input/KeyboardButton";
import cn from "app/utils/classnames";
import { useCallback } from "react";

interface KeyboardProps {
  // If true, the keyboard's keys will have a shorter height. Use this to
  // accommodate devices with shorter screens.
  useShortVersion?: boolean;
  
  onLetterDown?: (key: string) => void;
  onEnterDown?: () => void;
  onBackspaceDown?: () => void;
  
  // If true, the key down events will be sent for physical keypresses
  // as well. By default, 
  allowPhysKeyPresses?: boolean;
  
  disableInput?: boolean;
}

export default function Keyboard({
  useShortVersion = false,
  onLetterDown,
  onEnterDown,
  onBackspaceDown,
  allowPhysKeyPresses = false,
  disableInput = false,
}: KeyboardProps) {

  function handleLetterKeyPress(keyText: string) {
    onLetterDown?.(keyText);
  }

  const handleBackspaceDown = useCallback(() => {
    onBackspaceDown?.();
  }, [onBackspaceDown])

  return (
    <div className={cn("w-full p-0", "flex flex-col gap-1 lg:gap-3")}>
      <KeyRow isShort={useShortVersion}>
        {createLetterKeys(
          ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
          handleLetterKeyPress,
          disableInput ?? false,
          allowPhysKeyPresses
        )}
      </KeyRow>
      <KeyRow isShort={useShortVersion}>
        {createLetterKeys(
          ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
          handleLetterKeyPress,
          disableInput ?? false,
          allowPhysKeyPresses
        )}
      </KeyRow>
      <KeyRow isShort={useShortVersion}>
        <KeyboardButton
          text="Enter"
          onPress={() => onEnterDown?.()}
          className="basis-auto"
          callOnPressForPhysKeys={allowPhysKeyPresses}
        />
        {createLetterKeys(
          ["Z", "X", "C", "V", "B", "N", "M"],
          handleLetterKeyPress,
          disableInput ?? false,
          allowPhysKeyPresses
        )}
        <KeyboardButton
          text="⌫"
          onPress={handleBackspaceDown}
          physKeycode="Backspace"
          callOnPressForPhysKeys={allowPhysKeyPresses}
          className="text-2xl"
        />
      </KeyRow>
    </div>
  );
}

interface KeyRowProps {
  children: React.ReactNode;
  isShort: boolean;
}

// A single row of the keyboard
function KeyRow(props: KeyRowProps) {
  props.isShort ??= false;

  return (
    <div
      className={cn(
        "flex justify-center gap-1 lg:gap-2",
        "shrink",
        props.isShort ? "h-10" : "h-14",
      )}
    >
      {props.children}
    </div>
  );
}

// Sugar for creating multiple letter keys at once
function createLetterKeys(
  buttonLetters: Array<string>,
  onPress: (keyText: string) => void,
  disabled: boolean,
  allowPhysKeyPresses: boolean,
): Array<React.ReactNode> {
  return buttonLetters.map((letter, index) => (
    <KeyboardButton
      key={`${letter}-${index}`}
      text={letter}
      onPress={onPress}
      disabled={disabled}
      callOnPressForPhysKeys={allowPhysKeyPresses}
    />
  ));
}
